import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ClockCountdownIcon, SignOutIcon } from "@phosphor-icons/react";

// Auto-logout after inactivity, with warnings before the session ends.
const IDLE_LIMIT_MS = 30 * 60 * 1000; // 30 min
const WARN_MS = 5 * 60 * 1000; // first warning at 5 min remaining
const URGENT_MS = 2 * 60 * 1000; // escalated warning at 2 min remaining
const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click"];

const fmt = (ms) => {
  const s = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

export const IdleTimer = () => {
  const { user, logout } = useAuth();
  const active = !!user && typeof user === "object";
  const lastActivity = useRef(Date.now());
  const warnShown = useRef(false);
  const urgentShown = useRef(false);
  const [remaining, setRemaining] = useState(null); // ms left, or null when not warning

  useEffect(() => {
    if (!active) return;

    const bump = () => { lastActivity.current = Date.now(); };
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, bump, { passive: true }));

    const iv = setInterval(() => {
      const rem = IDLE_LIMIT_MS - (Date.now() - lastActivity.current);
      if (rem <= 0) {
        clearInterval(iv);
        toast.error("Signed out due to 30 minutes of inactivity");
        logout();
        return;
      }
      if (rem <= WARN_MS) {
        setRemaining(rem);
        if (!warnShown.current) {
          warnShown.current = true;
          toast.warning("Your session will expire in 5 minutes due to inactivity");
        }
        if (rem <= URGENT_MS && !urgentShown.current) {
          urgentShown.current = true;
          toast.error("2 minutes left — you're about to be signed out");
        }
      } else {
        if (warnShown.current || remaining !== null) setRemaining(null);
        warnShown.current = false;
        urgentShown.current = false;
      }
    }, 1000);

    return () => {
      clearInterval(iv);
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, bump));
    };
  }, [active, logout]); // eslint-disable-line react-hooks/exhaustive-deps

  const stay = () => {
    lastActivity.current = Date.now();
    warnShown.current = false;
    urgentShown.current = false;
    setRemaining(null);
  };

  if (!active || remaining === null) return null;
  const urgent = remaining <= URGENT_MS;

  return (
    <Dialog open onOpenChange={(o) => { if (!o) stay(); }}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 sm:max-w-sm" data-testid="idle-warning-dialog">
        <DialogHeader>
          <DialogTitle className={`flex items-center gap-2 font-head ${urgent ? "text-rose-400" : "text-amber-400"}`}>
            <ClockCountdownIcon weight="fill" className="h-5 w-5" /> Still there?
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            You've been inactive. For security, you'll be signed out automatically.
          </DialogDescription>
        </DialogHeader>
        <div className="py-2 text-center">
          <p className="text-xs uppercase tracking-widest text-zinc-600">Signing out in</p>
          <p data-testid="idle-countdown" className={`font-mono text-4xl font-bold ${urgent ? "text-rose-400" : "text-amber-300"}`}>
            {fmt(remaining)}
          </p>
        </div>
        <div className="flex gap-2">
          <button data-testid="idle-logout-now" onClick={logout}
            className="flex items-center justify-center gap-1.5 rounded-sm border border-zinc-700 px-3 py-2.5 text-sm text-zinc-300 hover:bg-zinc-800">
            <SignOutIcon className="h-4 w-4" /> Log out
          </button>
          <button data-testid="idle-stay-btn" onClick={stay}
            className="flex-1 rounded-sm bg-cyan-500 py-2.5 text-sm font-semibold text-zinc-950 hover:bg-cyan-400">
            Stay signed in
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
